// Team Hub / Pre-Combat Planner — slot-based layout (3 slots, 0=frontline, 2=backline).
//
// Two modes:
//   "Team Management" (no pendingCombat): all champions deployed, drag to reorder slots.
//   "Pre-Combat" (pendingCombat set): bench shows all champions; drag or click to
//     deploy up to the fighter limit. Enemy slots shown above. FIGHT to start.

import type { Scene } from "../engine/scene/SceneManager";
import type { Game } from "../game/Game";
import { showChampionTooltip, hideChampionTooltip } from "../ui/ChampionTooltip";
import { SlotLayout } from "../engine/SlotLayout";
import { championIconUrl } from "../config/constants";
import { drawSprite } from "../engine/render/Sprite";
import { CHAMPIONS, TIER_COLORS, TIER_STYLES, type ChampionTier } from "../data/champions";
import { levelForXp, effectiveStatsForLevel } from "../units/UnitStats";
import type { RosterEntry, StarLevel } from "../game/RunState";

function getStarMultHub(tier: ChampionTier, stars: StarLevel): number {
  const mults: Record<ChampionTier, [number, number, number]> = {
    common:    [1, 1.5, 2.0],
    rare:      [1, 1.7, 2.6],
    epic:      [1, 2.0, 3.2],
    legendary: [1, 2.0, 3.8],
  };
  return (mults[tier] ?? mults.common)[stars - 1] ?? 1;
}

function rosterMaxHpHub(entry: RosterEntry): number {
  const champ = CHAMPIONS[entry.championId];
  const level = levelForXp(entry.xp);
  const sm = getStarMultHub(champ.tier, entry.stars ?? 1);
  return effectiveStatsForLevel(
    { ...champ, baseHp: Math.round(champ.baseHp * sm), baseAd: Math.round(champ.baseAd * sm), baseAp: Math.round(champ.baseAp * sm) },
    level,
    champ.levelGrowth,
  ).maxHp;
}
import { MapScene } from "./MapScene";
import { CombatScene } from "./CombatScene";
import { AchievementsScene } from "./AchievementsScene";
import type { PointerEventData } from "../engine/input/InputManager";

const BENCH_HEIGHT = 84;
const TOP_BAR_HEIGHT = 46;
const DRAG_THRESHOLD_PX = 6;
const SLOT_HIT_MULT = 1.6; // slot hit radius = size * this

interface SlotDragState {
  rosterId: string;
  fromSlot: number;
  pointerX: number;
  pointerY: number;
  downX: number;
  downY: number;
  active: boolean;
}

interface BenchDragState {
  rosterId: string;
  ghost: HTMLDivElement;
  clientX: number;
  clientY: number;
}

export class TeamHubScene implements Scene {
  private root: HTMLDivElement;
  private topBar!: HTMLDivElement;
  private benchBar!: HTMLDivElement;

  private layout!: SlotLayout;

  private slotDrag: SlotDragState | null = null;
  private hoverSlot: number | null = null;
  private benchDrag: BenchDragState | null = null;
  private boundBenchMove?: (e: PointerEvent) => void;
  private boundBenchUp?: (e: PointerEvent) => void;

  private deployedIds = new Set<string>();
  private slotPipEls: HTMLElement[] = [];

  private bgCache: { canvas: HTMLCanvasElement; w: number; h: number; src: HTMLImageElement } | null = null;

  private unsubDown?: () => void;
  private unsubMove?: () => void;
  private unsubUp?: () => void;
  private unsubResize?: () => void;

  constructor(
    private game: Game,
    private afterCombat?: (winner: "player" | "enemy") => void,
    private onBack?: () => void,
  ) {
    this.root = document.createElement("div");
    this.root.className = "hub-screen-v2";
    this.root.style.pointerEvents = "none";
    this.computeLayout();
  }

  enter(): void {
    const state = this.game.runState!;
    const pending = state.pendingCombat;

    this.deployedIds.clear();
    if (!pending || pending.fighterLimit === null) {
      for (const e of state.roster) {
        if (e.currentHp !== null && e.currentHp <= 0) continue;
        this.deployedIds.add(e.id);
      }
    }

    this.buildDom();
    this.game.sceneRoot.appendChild(this.root);

    this.unsubDown   = this.game.input.onPointerDown((e) => this.onPointerDown(e));
    this.unsubMove   = this.game.input.onPointerMove((e) => this.onPointerMove(e));
    this.unsubUp     = this.game.input.onPointerUp((e) => this.onPointerUp(e));
    this.unsubResize = this.game.renderer.onResize(() => {
      this.computeLayout();
      this.refreshBench();
      this.bgCache = null;
    });

    this.refreshBench();
  }

  exit(): void {
    this.unsubDown?.();
    this.unsubMove?.();
    this.unsubUp?.();
    this.unsubResize?.();
    this.cancelBenchDrag();
    hideChampionTooltip();
    this.root.remove();
    this.bgCache = null;
  }

  update(_dt: number): void {}

  render(ctx: CanvasRenderingContext2D): void {
    this.drawBackground(ctx);
    this.drawSlotRows(ctx);
    if (this.isPrecombat) this.drawEnemies(ctx);
    this.drawDeployedChampions(ctx);
    this.drawSlotDragGhost(ctx);
  }

  private get isPrecombat(): boolean {
    return !!this.game.runState?.pendingCombat;
  }

  // ---- Layout ----

  private computeLayout(): void {
    const w = this.game.renderer.designW;
    const h = this.game.renderer.designH;
    const areaTop    = TOP_BAR_HEIGHT + 8;
    const areaBottom = h - BENCH_HEIGHT - 8;
    const combatY    = (areaTop + areaBottom) / 2;
    const xSpacing   = (w / 2) / 6;
    const unitSize   = Math.min(56, Math.max(32, xSpacing * 0.85));
    this.layout = new SlotLayout(w, combatY, unitSize);
  }

  private get gridAreaBottom(): number {
    return this.game.renderer.designH - BENCH_HEIGHT;
  }

  // Hit-test: which player slot (0-4) is closest to (x,y)? Only matches left half.
  private playerSlotAt(x: number, y: number): number | null {
    if (x >= this.layout.w / 2) return null; // player slots are on the left side only
    let best = -1, bestDist = Infinity;
    for (let s = 0; s < 3; s++) {
      const p = this.layout.slotToPixel(s, "player");
      const d = Math.hypot(x - p.x, y - p.y);
      if (d < this.layout.size * SLOT_HIT_MULT && d < bestDist) { bestDist = d; best = s; }
    }
    return best >= 0 ? best : null;
  }

  private championAtSlot(slot: number): RosterEntry | null {
    return this.game.runState?.roster.find(
      (r) => this.deployedIds.has(r.id) && r.pos.q === slot,
    ) ?? null;
  }

  // ---- DOM ----

  private buildDom(): void {
    this.topBar = document.createElement("div");
    this.topBar.className = "hub-topbar";

    const back = document.createElement("button");
    back.className = "btn btn-sm ghost";
    back.textContent = "BACK";
    back.addEventListener("click", () => {
      if (this.onBack) { this.onBack(); return; }
      if (this.game.runState) {
        this.game.runState.pendingCombat = null;
        this.game.runState.combatFighterIds = null;
      }
      this.game.sceneManager.transition(new MapScene(this.game));
    });

    const title = document.createElement("div");
    title.className = "hub-topbar-title";
    title.id = "hub-title";
    title.textContent = this.isPrecombat ? "POSITION TEAM" : "TEAM";

    const rightArea = document.createElement("div");
    rightArea.style.cssText = "display:flex;align-items:center;gap:8px;min-width:64px;justify-content:flex-end";

    const achBtn = document.createElement("button");
    achBtn.className = "btn btn-sm ghost";
    achBtn.textContent = "ACHIEVEMENTS";
    achBtn.style.pointerEvents = "auto";
    achBtn.addEventListener("click", () => {
      this.game.sceneManager.transition(
        new AchievementsScene(this.game, () => {
          this.game.sceneManager.transition(
            new TeamHubScene(this.game, this.afterCombat, this.onBack),
          );
        }),
      );
    });
    rightArea.appendChild(achBtn);

    const pipWrap = document.createElement("div");
    pipWrap.style.cssText = "display:flex;align-items:center;gap:6px;";
    const pipLabel = document.createElement("span");
    pipLabel.style.cssText = "font-size:9px;letter-spacing:1.5px;color:rgba(180,190,210,0.35);";
    pipLabel.textContent = "SLOTS";
    const pipRow = document.createElement("div");
    pipRow.className = "hub-slot-pips";
    this.slotPipEls = [];
    for (let i = 0; i < 3; i++) {
      const pip = document.createElement("div");
      pip.className = "hub-slot-pip";
      pipRow.appendChild(pip);
      this.slotPipEls.push(pip);
    }
    pipWrap.appendChild(pipLabel);
    pipWrap.appendChild(pipRow);
    rightArea.appendChild(pipWrap);

    this.topBar.appendChild(back);
    this.topBar.appendChild(title);
    this.topBar.appendChild(rightArea);
    this.root.appendChild(this.topBar);

    if (this.isPrecombat) {
      const fightBtn = document.createElement("button");
      fightBtn.className = "btn btn-sm";
      fightBtn.id = "fight-btn";
      fightBtn.textContent = "FIGHT";
      fightBtn.style.cssText =
        `position:absolute;bottom:calc(${BENCH_HEIGHT + 6}px + var(--ad-anchor-height));left:50%;transform:translateX(-50%);` +
        "z-index:3;pointer-events:auto;";
      fightBtn.addEventListener("click", () => this.onFightClick());
      this.root.appendChild(fightBtn);
    }

    this.benchBar = document.createElement("div");
    this.benchBar.className = "bench-bar";
    this.benchBar.style.pointerEvents = "auto";
    this.root.appendChild(this.benchBar);

    if (!this.isPrecombat) {
      const hint = document.createElement("div");
      hint.style.cssText =
        "position:absolute;bottom:calc(88px + var(--ad-anchor-height));left:50%;transform:translateX(-50%);" +
        "font-size:11px;color:rgba(240,230,210,0.45);pointer-events:none;white-space:nowrap;";
      hint.textContent = "Drag heroes to set starting positions";
      this.root.appendChild(hint);
    }
  }

  private syncSlotPips(): void {
    for (let i = 0; i < this.slotPipEls.length; i++) {
      this.slotPipEls[i].classList.toggle("filled", this.championAtSlot(i) !== null);
    }
  }

  private refreshBench(): void {
    if (!this.benchBar) return;
    const state = this.game.runState;
    if (!state) return;

    const pending = state.pendingCombat;
    const limit   = pending?.fighterLimit ?? null;
    const limitReached = limit !== null && this.deployedIds.size >= limit;
    const isJungle = pending?.node.type === "jungle";

    const title = this.root.querySelector<HTMLElement>("#hub-title");
    if (title) {
      if (!pending) {
        title.textContent = "TEAM";
      } else if (limit === null) {
        title.textContent = "POSITION TEAM";
      } else if (isJungle) {
        const sel = this.deployedIds.size;
        title.textContent = sel === 0 ? "CLICK A CHAMPION TO FIGHT" : `${sel}/${limit} READY`;
      } else {
        const sel = this.deployedIds.size;
        title.textContent = sel === 0 ? `DEPLOY UP TO ${limit}` : `${sel}/${limit} READY`;
      }
    }

    const fightBtn = this.root.querySelector<HTMLButtonElement>("#fight-btn");
    if (fightBtn) {
      const ready = limit === null || this.deployedIds.size >= 1;
      fightBtn.disabled = !ready;
      fightBtn.style.opacity = ready ? "1" : "0.4";
    }

    this.benchBar.innerHTML = "";
    for (const entry of state.roster) {
      if (entry.currentHp !== null && entry.currentHp <= 0) continue;
      const deployed = this.deployedIds.has(entry.id);
      const unavailable = !deployed && limitReached;
      const def = CHAMPIONS[entry.championId];
      const level = levelForXp(entry.xp);

      const icon = document.createElement("div");
      icon.className = "bench-icon" + (deployed ? " deployed" : "") + (unavailable ? " unavailable" : "");
      icon.dataset.rosterId = entry.id;
      icon.style.backgroundImage = `url('${championIconUrl(def.iconId)}')`;
      icon.title = `${def.displayName} Lv${level}`;
      const tierColor = TIER_COLORS[def.tier];
      const ts = TIER_STYLES[def.tier];
      icon.dataset.tier = def.tier;
      icon.style.setProperty("--tc", tierColor);
      icon.style.outline = `${ts.borderPx}px solid ${tierColor}`;
      icon.style.outlineOffset = "1px";

      if (entry.currentHp !== null) {
        const maxHp = rosterMaxHpHub(entry);
        const ratio = Math.max(0, Math.min(1, entry.currentHp / maxHp));
        const hpBar = document.createElement("div");
        hpBar.style.cssText =
          "position:absolute;bottom:0;left:0;right:0;height:4px;background:#0e101a;border-radius:0 0 4px 4px;overflow:hidden;";
        const hpFill = document.createElement("div");
        const hpColor = ratio > 0.5 ? "#3da35a" : ratio > 0.25 ? "#c87a3d" : "#c8413d";
        hpFill.style.cssText = `height:100%;width:${Math.round(ratio * 100)}%;background:${hpColor};`;
        hpBar.appendChild(hpFill);
        icon.appendChild(hpBar);
      }

      {
        const stars = entry.stars ?? 1;
        const starColor = stars === 3 ? "#ffe066" : stars === 2 ? "#f0c040" : "#9aaabf";
        if (stars > 1) {
          icon.style.outline = `2px solid ${starColor}`;
          icon.style.boxShadow = `0 0 10px ${starColor}88`;
        }
        const badge = document.createElement("div");
        badge.style.cssText =
          "position:absolute;bottom:-6px;left:50%;transform:translateX(-50%);" +
          `background:rgba(0,0,0,0.85);border:1px solid ${starColor}88;border-radius:8px;` +
          `padding:1px 5px;font-size:9px;line-height:1.4;color:${starColor};` +
          `pointer-events:none;z-index:4;white-space:nowrap;` +
          (stars > 1 ? `text-shadow:0 0 5px ${starColor};` : "");
        badge.textContent = stars > 1 ? `Lv${level} ${"★".repeat(stars)}` : `Lv${level}`;
        icon.appendChild(badge);
      }

      icon.addEventListener("click", (e) => {
        e.stopPropagation();
        showChampionTooltip(entry.championId, levelForXp(entry.xp), icon);
      });

      // Delete (kick) button — team management mode only, and never the last champion.
      if (!this.isPrecombat && state.roster.length > 1) {
        const del = document.createElement("button");
        del.className = "bench-delete";
        del.title = `Kick ${def.displayName} from the team`;
        del.textContent = "×";
        del.addEventListener("pointerdown", (e) => {
          e.stopPropagation();
        });
        del.addEventListener("click", (e) => {
          e.stopPropagation();
          this.openKickConfirm(entry);
        });
        icon.appendChild(del);
      }

      if (!deployed && !unavailable) {
        icon.addEventListener("pointerdown", (e) => {
          e.stopPropagation();
          this.startBenchDrag(e, entry);
        });
        if (isJungle) {
          icon.style.cursor = "pointer";
          icon.addEventListener("click", (e) => {
            e.stopPropagation();
            if (this.deployedIds.has(entry.id)) return;
            this.autoDeployChampion(entry);
          });
          icon.title = `${def.displayName} Lv${level} — click to fight`;
        }
      }

      this.benchBar.appendChild(icon);
    }
    this.syncSlotPips();
  }

  // Confirmation modal: kick the champion from the roster permanently.
  private openKickConfirm(entry: RosterEntry): void {
    const state = this.game.runState;
    if (!state) return;
    const def = CHAMPIONS[entry.championId];
    const tierColor = TIER_COLORS[def.tier];

    const overlay = document.createElement("div");
    overlay.className = "kick-modal-overlay";

    const dialog = document.createElement("div");
    dialog.className = "kick-modal";
    dialog.style.setProperty("--tc", tierColor);

    const portrait = document.createElement("div");
    portrait.className = "kick-modal-portrait";
    portrait.style.backgroundImage = `url('${championIconUrl(def.iconId)}')`;

    const title = document.createElement("div");
    title.className = "kick-modal-title";
    title.textContent = "Kick from team?";

    const sub = document.createElement("div");
    sub.className = "kick-modal-sub";
    sub.innerHTML = `<span style="color:${tierColor}">${def.displayName}</span> will be removed from your roster permanently.`;

    const btnRow = document.createElement("div");
    btnRow.className = "kick-modal-btns";

    const cancel = document.createElement("button");
    cancel.className = "btn btn-sm ghost";
    cancel.textContent = "CANCEL";

    const confirm = document.createElement("button");
    confirm.className = "btn btn-sm danger";
    confirm.textContent = "KICK";

    const close = () => overlay.remove();
    cancel.addEventListener("click", close);
    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) close();
    });
    confirm.addEventListener("click", () => {
      state.removeFromRoster(entry.id);
      this.deployedIds.delete(entry.id);
      close();
      this.refreshBench();
    });

    btnRow.append(cancel, confirm);
    dialog.append(portrait, title, sub, btnRow);
    overlay.appendChild(dialog);
    this.root.appendChild(overlay);
  }

  // Deploy a champion to the first free slot (frontline-biased for melee)
  private autoDeployChampion(entry: RosterEntry): void {
    const state = this.game.runState;
    if (!state) return;
    const champ = CHAMPIONS[entry.championId];
    // Melee prefers front slots, ranged prefers back slots
    const preferred = champ.range <= 1
      ? [0, 1, 2, 3, 4]
      : [4, 3, 2, 1, 0];
    for (const slot of preferred) {
      if (!state.roster.some((r) => r.id !== entry.id && this.deployedIds.has(r.id) && r.pos.q === slot)) {
        state.moveToSlot(entry.id, slot);
        this.deployedIds.add(entry.id);
        this.refreshBench();
        return;
      }
    }
  }

  // ---- Fight button ----

  private onFightClick(): void {
    const state = this.game.runState;
    if (!state?.pendingCombat) return;
    const limit = state.pendingCombat.fighterLimit;
    if (limit !== null && this.deployedIds.size < 1) return;

    state.combatFighterIds = limit !== null ? [...this.deployedIds] : null;
    this.game.sceneManager.transition(
      new CombatScene(this.game, state.pendingCombat.node, this.afterCombat),
    );
  }

  // ---- Bench drag (bench → grid slot) ----

  private startBenchDrag(e: PointerEvent, entry: RosterEntry): void {
    this.cancelBenchDrag();
    const def = CHAMPIONS[entry.championId];
    const ghost = document.createElement("div");
    ghost.className = "bench-drag-ghost";
    ghost.style.backgroundImage = `url('${championIconUrl(def.iconId)}')`;
    ghost.style.left = `${e.clientX}px`;
    ghost.style.top  = `${e.clientY}px`;
    document.body.appendChild(ghost);
    this.benchDrag = { rosterId: entry.id, ghost, clientX: e.clientX, clientY: e.clientY };
    this.boundBenchMove = (ev) => this.onBenchDragMove(ev);
    this.boundBenchUp   = (ev) => this.onBenchDragUp(ev);
    window.addEventListener("pointermove", this.boundBenchMove);
    window.addEventListener("pointerup",   this.boundBenchUp);
  }

  private onBenchDragMove(e: PointerEvent): void {
    if (!this.benchDrag) return;
    this.benchDrag.clientX = e.clientX;
    this.benchDrag.clientY = e.clientY;
    this.benchDrag.ghost.style.left = `${e.clientX}px`;
    this.benchDrag.ghost.style.top  = `${e.clientY}px`;
  }

  private onBenchDragUp(e: PointerEvent): void {
    const drag = this.benchDrag;
    this.cancelBenchDrag();
    if (!drag) return;
    const state = this.game.runState;
    if (!state) return;

    const rect = this.game.renderer.canvas.getBoundingClientRect();
    const dx = e.clientX - rect.left;
    const dy = e.clientY - rect.top;

    const slot = this.playerSlotAt(dx, dy);
    if (slot !== null) {
      const occupant = this.championAtSlot(slot);
      if (occupant && occupant.id !== drag.rosterId) {
        // Swap: move occupant out, move dragged in
        const freeSlot = this.findFreeSlot(drag.rosterId);
        if (freeSlot !== null) state.moveToSlot(occupant.id, freeSlot);
      }
      state.moveToSlot(drag.rosterId, slot);
      this.deployedIds.add(drag.rosterId);
      this.refreshBench();
    }
  }

  private cancelBenchDrag(): void {
    this.benchDrag?.ghost.remove();
    this.benchDrag = null;
    if (this.boundBenchMove) window.removeEventListener("pointermove", this.boundBenchMove);
    if (this.boundBenchUp)   window.removeEventListener("pointerup",   this.boundBenchUp);
  }

  private findFreeSlot(excludeRosterId: string): number | null {
    for (let s = 0; s < 3; s++) {
      const occ = this.game.runState?.roster.find(
        (r) => r.id !== excludeRosterId && this.deployedIds.has(r.id) && r.pos.q === s,
      );
      if (!occ) return s;
    }
    return null;
  }

  // ---- Slot drag (grid slot → grid slot) ----

  private onPointerDown(e: PointerEventData): void {
    hideChampionTooltip();
    const slot = this.playerSlotAt(e.x, e.y);
    if (slot === null) return;
    const occupant = this.championAtSlot(slot);
    if (!occupant) return;
    this.slotDrag = {
      rosterId: occupant.id,
      fromSlot: slot,
      pointerX: e.x,
      pointerY: e.y,
      downX: e.x,
      downY: e.y,
      active: false,
    };
  }

  private onPointerMove(e: PointerEventData): void {
    if (!this.slotDrag) {
      this.hoverSlot = this.playerSlotAt(e.x, e.y);
      return;
    }
    this.slotDrag.pointerX = e.x;
    this.slotDrag.pointerY = e.y;
    if (!this.slotDrag.active) {
      const dx = e.x - this.slotDrag.downX;
      const dy = e.y - this.slotDrag.downY;
      if (Math.hypot(dx, dy) > DRAG_THRESHOLD_PX) this.slotDrag.active = true;
    }
  }

  private onPointerUp(e: PointerEventData): void {
    const drag = this.slotDrag;
    this.slotDrag = null;
    this.hoverSlot = null;
    if (!drag || !drag.active) return;

    const state = this.game.runState;
    if (!state) return;

    // Dropped in bench area → undeploy (only for limited fights)
    if (e.y >= this.gridAreaBottom && state.pendingCombat?.fighterLimit !== null) {
      this.deployedIds.delete(drag.rosterId);
      this.refreshBench();
      return;
    }

    const targetSlot = this.playerSlotAt(e.x, e.y);
    if (targetSlot === null || targetSlot === drag.fromSlot) return;

    const occupant = this.championAtSlot(targetSlot);
    if (occupant) {
      state.swapSlots(drag.rosterId, occupant.id);
    } else {
      state.moveToSlot(drag.rosterId, targetSlot);
    }
    this.refreshBench();
  }

  // ---- Canvas drawing ----

  private drawBackground(ctx: CanvasRenderingContext2D): void {
    const img = this.game.assets.get(`${import.meta.env.BASE_URL}images/map/background.png`);
    const w = this.game.renderer.designW;
    const h = this.game.renderer.designH;

    // Firefox's canvas filter is very slow; rebuilding a 60px blur every frame
    // tanks the framerate. Render the blurred image once into an offscreen
    // canvas and just blit it each frame.
    const cache = this.bgCache;
    const ready = img.naturalWidth > 0;
    if (ready && (!cache || cache.w !== w || cache.h !== h || cache.src !== img)) {
      const off = document.createElement("canvas");
      off.width = w;
      off.height = h;
      const octx = off.getContext("2d");
      if (octx) {
        octx.filter = "blur(60px) brightness(0.6)";
        octx.drawImage(img, 0, 0, w, h);
      }
      this.bgCache = { canvas: off, w, h, src: img };
    }

    if (this.bgCache) {
      ctx.drawImage(this.bgCache.canvas, 0, 0);
    }
  }

  private drawSlotRows(ctx: CanvasRenderingContext2D): void {
    const sz = this.layout.size;
    const w  = this.layout.w;
    const cy = this.layout.combatY;
    const draggingSlot = this.slotDrag?.active ? this.slotDrag.fromSlot : -1;

    // Background tint: left half = player, right half = enemy
    const laneH = sz * 3.2;
    ctx.save();
    ctx.fillStyle = "rgba(60,120,200,0.05)";
    ctx.fillRect(0, cy - laneH / 2, w / 2, laneH);
    ctx.fillStyle = "rgba(180,60,60,0.05)";
    ctx.fillRect(w / 2, cy - laneH / 2, w / 2, laneH);

    // Centre divider
    ctx.strokeStyle = "rgba(200,200,200,0.10)";
    ctx.lineWidth = 1;
    ctx.setLineDash([5, 5]);
    const h = this.game.renderer.designH;
    ctx.beginPath();
    ctx.moveTo(w / 2, TOP_BAR_HEIGHT);
    ctx.lineTo(w / 2, h - BENCH_HEIGHT);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();

    // Player slot circles
    for (let s = 0; s < 3; s++) {
      const p = this.layout.slotToPixel(s, "player");
      const isHover  = this.hoverSlot === s && !this.slotDrag;
      const isDrag   = draggingSlot === s;
      const isFilled = this.championAtSlot(s) !== null;

      ctx.save();
      ctx.beginPath();
      ctx.arc(p.x, p.y, sz * 0.9, 0, Math.PI * 2);
      ctx.fillStyle = isDrag
        ? "rgba(200,160,60,0.06)"
        : isFilled ? "rgba(60,120,200,0.08)" : "rgba(40,60,100,0.05)";
      ctx.fill();
      ctx.strokeStyle = isDrag
        ? "rgba(200,160,60,0.4)"
        : isHover ? "rgba(200,200,200,0.5)"
        : isFilled ? "rgba(80,140,200,0.3)"
        : "rgba(80,100,140,0.18)";
      ctx.lineWidth = 1.5;
      ctx.stroke();
      if (!isFilled) {
        ctx.fillStyle = "rgba(180,190,210,0.22)";
        ctx.font = `bold ${Math.round(sz * 0.65)}px 'Inter', sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(`${s + 1}`, p.x, p.y);
      }
      ctx.restore();
    }

    // Enemy slot outlines (pre-combat only)
    if (this.isPrecombat) {
      for (let s = 0; s < 3; s++) {
        const p = this.layout.slotToPixel(s, "enemy");
        ctx.save();
        ctx.beginPath();
        ctx.arc(p.x, p.y, sz * 0.9, 0, Math.PI * 2);
        ctx.strokeStyle = "rgba(200,80,80,0.15)";
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.restore();
      }
    }

    // FRONT / BACK labels under player slots
    const fl = this.layout.slotToPixel(0, "player");
    const bl = this.layout.slotToPixel(2, "player");
    ctx.save();
    ctx.fillStyle = "rgba(180,190,210,0.35)";
    ctx.font = "bold 9px 'Inter', sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("FRONT", fl.x, cy + sz * 1.5);
    ctx.fillText("BACK",  bl.x, cy + sz * 1.5);
    ctx.restore();
  }

  private drawEnemies(ctx: CanvasRenderingContext2D): void {
    const enemies = this.game.runState?.pendingCombat?.enemies;
    if (!enemies) return;
    const sz = this.layout.size * 2;

    // Sort by slot so we draw in slot order
    const sorted = [...enemies].sort((a, b) => a.pos.q - b.pos.q);
    for (let i = 0; i < sorted.length; i++) {
      const enemy = sorted[i];
      const p = this.layout.slotToPixel(enemy.pos.q, "enemy");
      const sy = p.y - sz * 0.15;
      this.drawShadow(ctx, p.x, p.y, sz);
      const img = this.game.assets.get(enemy.def.iconUrl);
      drawSprite(ctx, img, { x: p.x, y: sy, width: sz, height: sz, pivotX: 0.5, pivotY: 0.5, circular: true });
      if (enemy.championDef) this.drawRarityRing(ctx, p.x, sy, sz, enemy.championDef.tier);
      this.drawLevelPip(ctx, p.x, sy, sz, enemy.level);
    }
  }

  private drawDeployedChampions(ctx: CanvasRenderingContext2D): void {
    const state = this.game.runState;
    if (!state) return;
    const sz = this.layout.size * 2;
    const draggingId = this.slotDrag?.active ? this.slotDrag.rosterId : null;

    for (const entry of state.roster) {
      if (!this.deployedIds.has(entry.id)) continue;
      if (entry.id === draggingId) continue;

      const def = CHAMPIONS[entry.championId];
      const p = this.layout.slotToPixel(entry.pos.q, "player");
      const sy = p.y - sz * 0.15;
      this.drawShadow(ctx, p.x, p.y, sz);
      const img = this.game.assets.get(championIconUrl(def.iconId));
      drawSprite(ctx, img, { x: p.x, y: sy, width: sz, height: sz, pivotX: 0.5, pivotY: 0.5, circular: true });
      this.drawRarityRing(ctx, p.x, sy, sz, def.tier, entry.stars ?? 1);
      const entryLevel = levelForXp(entry.xp);
      this.drawLevelPip(ctx, p.x, sy, sz, entryLevel);
      this.drawStarBadge(ctx, p.x, sy, sz, entry.stars ?? 1, entryLevel);
      this.drawCooldownPips(ctx, p.x, sy, sz, def.abilityCooldown ?? 0);
    }
  }

  private drawSlotDragGhost(ctx: CanvasRenderingContext2D): void {
    if (!this.slotDrag?.active) return;
    const state = this.game.runState;
    if (!state) return;
    const entry = state.roster.find((r) => r.id === this.slotDrag!.rosterId);
    if (!entry) return;
    const def = CHAMPIONS[entry.championId];
    const sz = this.layout.size * 2;

    // Ghost at source slot
    const src = this.layout.slotToPixel(this.slotDrag.fromSlot, "player");
    ctx.save();
    ctx.globalAlpha = 0.25;
    drawSprite(ctx, this.game.assets.get(championIconUrl(def.iconId)), {
      x: src.x, y: src.y, width: sz, height: sz, pivotX: 0.5, pivotY: 0.5, circular: true,
    });
    ctx.restore();

    // Live ghost at pointer
    drawSprite(ctx, this.game.assets.get(championIconUrl(def.iconId)), {
      x: this.slotDrag.pointerX,
      y: this.slotDrag.pointerY,
      width: sz * 1.1,
      height: sz * 1.1,
      pivotX: 0.5,
      pivotY: 0.5,
      circular: true,
    });
  }

  // ---- Draw helpers ----

  private drawShadow(ctx: CanvasRenderingContext2D, x: number, y: number, sz: number): void {
    ctx.save();
    ctx.translate(x, y + sz * 0.35);
    ctx.scale(1, 0.35);
    ctx.beginPath();
    ctx.arc(0, 0, sz * 0.5, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(0,0,0,0.5)";
    ctx.fill();
    ctx.restore();
  }

  private drawRarityRing(ctx: CanvasRenderingContext2D, x: number, y: number, sz: number, tier: ChampionTier, stars = 1): void {
    const tierColor = TIER_COLORS[tier];
    const ringColor = stars === 3 ? "#ffe066" : stars === 2 ? "#f0c040" : tierColor;
    const ts = TIER_STYLES[tier];
    ctx.save();
    ctx.globalAlpha = 0.9;
    ctx.strokeStyle = ringColor;
    ctx.lineWidth = stars >= 2 ? 3.5 : Math.max(1.5, ts.borderPx - 0.5);
    ctx.shadowColor = ringColor;
    ctx.shadowBlur = stars >= 2 ? 14 : ts.glowBlur;
    ctx.beginPath();
    ctx.arc(x, y, sz * 0.52, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  private drawStarBadge(ctx: CanvasRenderingContext2D, x: number, y: number, sz: number, stars: number, level = 0): void {
    if (stars <= 1) return;
    const starStr = level > 0 ? `Lv${level} ${"★".repeat(stars)}` : "★".repeat(stars);
    const starColor = stars === 3 ? "#ffe066" : "#f0c040";
    const fontSize = 11;
    const badgeY = y + sz * 0.56;
    ctx.save();
    ctx.font = `bold ${fontSize}px 'Inter', sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    const textW = ctx.measureText(starStr).width;
    const padX = 5, padY = 3;
    ctx.fillStyle = "rgba(0,0,0,0.75)";
    ctx.beginPath();
    ctx.roundRect(x - textW / 2 - padX, badgeY - (fontSize + padY * 2) / 2, textW + padX * 2, fontSize + padY * 2, (fontSize + padY * 2) / 2);
    ctx.fill();
    ctx.shadowColor = starColor;
    ctx.shadowBlur = 8;
    ctx.fillStyle = starColor;
    ctx.fillText(starStr, x, badgeY);
    ctx.restore();
  }

  private drawCooldownPips(ctx: CanvasRenderingContext2D, x: number, y: number, sz: number, cooldown: number): void {
    if (cooldown <= 0) return;
    const n = cooldown;
    const TOTAL_W = 46;
    const PIP_H = 6;
    const PIP_GAP = 3;
    const pipW = (TOTAL_W - PIP_GAP * (n - 1)) / n;
    const startX = x - TOTAL_W / 2;
    // position just above the rarity ring top
    const barY = y - sz * 0.52 - PIP_H - 6;
    ctx.save();
    for (let i = 0; i < n; i++) {
      const px = startX + i * (pipW + PIP_GAP);
      ctx.shadowBlur = 0;
      ctx.fillStyle = "#0e101a";
      ctx.fillRect(px, barY, pipW, PIP_H);
      ctx.strokeStyle = "#3a3a5a";
      ctx.lineWidth = 1;
      ctx.strokeRect(px + 0.5, barY + 0.5, pipW - 1, PIP_H - 1);
    }
    const label = `${n}`;
    ctx.font = "bold 8px 'Inter', sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "rgba(200,180,100,0.9)";
    ctx.fillText(label, x + TOTAL_W / 2 + 7, barY + PIP_H / 2);
    ctx.restore();
  }

  private drawLevelPip(ctx: CanvasRenderingContext2D, x: number, y: number, sz: number, level: number): void {
    ctx.save();
    ctx.fillStyle = "#000";
    ctx.globalAlpha = 0.7;
    ctx.fillRect(x - sz * 0.45, y + sz * 0.3, 22, 12);
    ctx.globalAlpha = 1;
    ctx.fillStyle = "#f0e6d2";
    ctx.font = "bold 9px 'Inter', sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(`Lv${level}`, x - sz * 0.45 + 11, y + sz * 0.3 + 6);
    ctx.restore();
  }

}
